import { Box, Button, CircularProgress, Modal, TextField } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const NewUserModal = ({ isOpen, onClose, userRegister }) => {
    const [hashValue, setHashValue] = useState('');
    const [hashError, setHashError] = useState(false);
    const [expirationDate, setExpirationDate] = useState(dayjs());
    const [status, setStatus] = useState('form'); // form | loading | result
    const [result, setResult] = useState(null);   // Guarda data o error    

    // Función para convertir "DD/MM/YYYY" → "YYYY-MM-DD"
    const transformDate = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/').map(Number);
        return dayjs(`${year}-${month}-${day}`); // Devuelve formato compatible
    };

    const extendExpiration = (months) => {
        setExpirationDate(prev => prev.add(months, 'month'));
    };

    const onSave = async () => {
        setStatus('loading');
        const response = await userRegister(hashValue, expirationDate.format("DD/MM/YYYY"));
    
        setResult({
            success: response.status === 201,
            code: response.status,
            data: response.data
        });
    
        setStatus('result');
    };
    
    const isExpired = expirationDate && expirationDate.isBefore(dayjs(), 'day');

    const renderResult = () => {
        if (!result) return null;

        const iconStyle = { fontSize: '3.5rem' };
        const getIcon = () => {
            if (result.success) return <span className="material-symbols-rounded" style={{ ...iconStyle, color: 'var(--color-success)' }}>check</span>;
            if (result.code === 409) return <span className="material-symbols-rounded" style={{ ...iconStyle, color: 'var(--color-warning)' }}>warning</span>;
            return <span className="material-symbols-rounded" style={{ ...iconStyle, color: 'var(--color-danger)' }}>error</span>;
        };

        const getMessage = () => {
            if (result.success) return "Usuario registrado correctamente.";
            switch (result.code) {
                case 400: return "Faltan datos o hash vacío.";
                case 401: return "Hash inválido.";
                case 403: return "No tienes permisos para realizar esta operación.";
                case 409: return "El usuario ya se encuentra registrado.";
                case 502: return "No se pudo autenticar el token.";
                default: return "Error al registrar el usuario.";
            }
        };

        return (
            <Box textAlign="center" >
                {getIcon()}
                <h2>{getMessage()}</h2>
                {result.data && (
                    <Box mt={2} sx={{ fontSize: '0.9rem', color: 'gray' }}>
                        <p><strong>Nombre:</strong> {result.data.name}</p>
                        <p><strong>Email:</strong> {result.data.email}</p>
                        <p><strong>Expira:</strong> {result.data.expirationDate}</p>
                    </Box>
                )}
                <Box mt={3}>
                    <Button variant="contained" onClick={() => onClose(false)}>Cerrar</Button>
                </Box>
            </Box>
        );
    };

    return (
        <Modal open={isOpen} onClose={() => onClose(false)}>
            <Box className="edit_date_modal">
                {status === 'form' && (
                    <>
                        <h2 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Registrar usuario</h2>

                        <TextField
                            fullWidth
                            label="Hash del usuario"
                            variant="outlined"
                            value={hashValue}
                            autoComplete='off'
                            autoFocus={true}
                            onChange={(e) => {
                                e.target.value.length > 0 && e.target.value.length < 32 ? setHashError(true) : setHashError(false)

                                setHashValue(e.target.value);
                            }}
                            error={hashError} // Muestra el borde rojo si hay un error
                            helperText={hashError ? 'El Hash debe tener 32 caracteres.' : ""} // Muestra el mensaje de error
                            slotProps={{ htmlInput: { maxLength: 32 } }}
                            sx={{
                                '& .MuiInputLabel-root': {
                                    color: 'var(--color-dark)', // Cambia el color de la label
                                    fontSize: '0.8rem',
                                },
                                '& .MuiInputLabel-shrink': {
                                    fontSize: '1rem', // Tamaño de la label cuando está en foco
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                                    },
                                    '&:not(.Mui-focused):hover fieldset': {
                                        borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    color: 'var(--color-dark)',
                                    fontSize: '0.8rem', // Tamaño del texto de entrada
                                },
                                marginBottom: '0.5rem'
                            }}
                        />

                        {/* Date Picker de Material UI */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Fecha de expiración"
                                value={expirationDate}
                                minDate={dayjs()} // No permite fechas anteriores a hoy
                                onChange={(newValue) => setExpirationDate(newValue)}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        error: isExpired,
                                        helperText: isExpired ? "Fecha vencida" : "",
                                        fullWidth: true
                                    }
                                }}
                                sx={{
                                    '& .MuiInputLabel-root': {
                                        color: 'var(--color-dark)',
                                        // fontSize: '0.7rem' // Tamaño de la etiqueta
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: 'var(--color-dark-variant)' },
                                        '&:hover fieldset': { borderColor: 'var(--color-dark)' },
                                        '&.Mui-focused fieldset': { borderColor: 'var(--color-dark)' },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: 'var(--color-dark)',
                                        fontSize: '0.8rem', // Tamaño del texto de entrada
                                    },
                                    '& .MuiSvgIcon-root': { color: 'var(--color-dark)' }, // Color del ícono
                                }}
                            />
                        </LocalizationProvider>

                        {/* Botones de ajuste rápido */}
                        <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, flexWrap: 'wrap' }}>
                            <Button variant="contained" sx={{ flex: 1, whiteSpace: 'nowrap' }} onClick={() => extendExpiration(1)}>+1 Mes</Button>
                            <Button variant="contained" sx={{ flex: 1, whiteSpace: 'nowrap' }} onClick={() => extendExpiration(3)}>+3 Meses</Button>
                            <Button variant="contained" sx={{ flex: 1, whiteSpace: 'nowrap' }} onClick={() => extendExpiration(6)}>+6 Meses</Button>
                            <Button variant="contained" sx={{ flex: 1, whiteSpace: 'nowrap' }} onClick={() => extendExpiration(12)}>+1 Año</Button>
                        </Box>

                        {/* Botones de guardar y cancelar */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 2 }}>
                            <Button onClick={() => onClose(false)} color="primary">Cancelar</Button>
                            <Button onClick={onSave} style={{ backgroundColor: 'var(--color-success)' }} variant="contained" disabled={isExpired || hashValue.trim() === '' || hashValue.length !== 32}>
                                Registrar
                            </Button>
                        </Box>
                    </>
                )}

                {status === 'loading' && (
                    <Box textAlign="center" py={10}>
                        <CircularProgress />
                    </Box>
                )}

                {status === 'result' && renderResult()}
            </Box>
        </Modal>
    )
}

export default NewUserModal